import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label, cad, errMsg, isPreviewHost } from "@/components/sponsored/format";
import { RefundPanel } from "@/components/sponsored/RefundPanel";

function Balance({ d }) {
  const f = d.funds;
  const rows = [["Set aside for Shop Again", f.reserved_cad, "reserved"], ["Pending shopper rewards", f.pending_cad, "pending"],
    ["Paid to shoppers", f.settled_cad, "settled"], ["Refund in review", f.refund_hold_cad, "refund-hold"], ["Refunded to card", f.refunded_cad, "refunded"]];
  return (
    <div>
      <Label>Available budget</Label>
      <div className="font-display text-4xl font-medium tabular-nums text-[#004C3F] mt-1" data-testid="sc-available">{cad(f.available_cad)}</div>
      <p className="text-xs text-slate-500 mt-1" data-testid="sc-covered">Covers {d.shoppers_covered_now} more returning shopper{d.shoppers_covered_now === 1 ? "" : "s"} at your current limit.</p>
      <dl className="mt-4 divide-y divide-slate-100 text-sm">
        {rows.map(([k, v, t]) => (
          <div key={t} className="flex justify-between py-1.5"><dt className="text-slate-600">{k}</dt><dd className="tabular-nums font-semibold" data-testid={`sc-fund-${t}`}>{cad(v)}</dd></div>
        ))}
      </dl>
    </div>
  );
}

function TopUp({ s }) {
  const [amount, setAmount] = useState(String(s.topup_presets_cad[1]));
  const [custom, setCustom] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const ok = Number.isInteger(n) && n >= s.min_topup_cad && n <= s.max_topup_cad;
  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/merchant/sponsored-cashback/topups", { amount_cad: amount, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errMsg(e, "Couldn't start the card payment"));
      setBusy(false);
    }
  };
  const pill = (on) => `px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#004C3F] text-white border-[#004C3F]" : "bg-white/70 border-slate-200 hover:border-[#004C3F]/50"}`;
  return (
    <div className="space-y-3" data-testid="sc-topup">
      <Label>Add to your budget</Label>
      <div className="flex flex-wrap gap-2">
        {s.topup_presets_cad.map((p) => (
          <button key={p} type="button" className={pill(!custom && n === p)} onClick={() => { setCustom(false); setAmount(String(p)); }} data-testid={`sc-topup-preset-${p}`}>CA${p}</button>
        ))}
        <button type="button" className={pill(custom)} onClick={() => { setCustom(true); setAmount(""); }} data-testid="sc-topup-custom-btn">Other amount</button>
      </div>
      {custom && <input className="pf-input w-full px-3 py-2 text-sm" inputMode="numeric" placeholder={`Whole dollars, CA$${s.min_topup_cad}–${s.max_topup_cad.toLocaleString()}`}
        aria-label="Top-up amount in Canadian dollars" aria-invalid={amount !== "" && !ok}
        value={amount} onChange={(e) => setAmount(e.target.value.trim())} data-testid="sc-topup-custom-input" />}
      {custom && amount !== "" && !ok && (
        <p className="text-xs text-rose-600" data-testid="sc-topup-amount-error">Enter a whole-dollar amount from CA${s.min_topup_cad} to CA${s.max_topup_cad.toLocaleString()}.</p>
      )}
      <button onClick={pay} disabled={!ok || busy} className="pf-btn-primary w-full px-5 py-2.5 rounded-full text-sm disabled:opacity-50" data-testid="sc-topup-pay-btn">
        {busy ? "Opening secure checkout…" : ok ? `Add CA$${n.toLocaleString()} by card` : "Choose an amount"}
      </button>
      {isPreviewHost() && (
        <p className="text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5" data-testid="sc-topup-test-note">
          Preview / Test: Stripe test mode, no real charge. Use test card 4242 4242 4242 4242.
        </p>
      )}
      <p className="text-[11px] text-slate-500" data-testid="sc-topup-currency-note">Paid by card through Stripe and always charged in Canadian dollars. If checkout offers to show another currency, choose CA$ — your budget is credited in CAD. Unused budget stays yours, even when the offer is off.</p>
    </div>
  );
}

function Alerts({ d, onChange }) {
  const toggle = async (v) => {
    try { await api.patch("/merchant/sponsored-cashback/notifications", { sponsored_cashback_alerts: v }); onChange(); }
    catch (e) { toast.error(errMsg(e, "Couldn't update alerts")); }
  };
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
      Low-budget alerts and weekly summary
      <Switch checked={d.alerts_enabled} onCheckedChange={toggle} data-testid="sc-alerts-switch" />
    </label>
  );
}

export function BudgetCard({ d, onChange }) {
  return (
    <section className="pf-card p-6 space-y-6" data-testid="sc-budget-card">
      <h3 className="font-display text-xl font-medium text-slate-900">Sponsored Cashback Budget</h3>
      <Balance d={d} />
      <TopUp s={d.settings} />
      <RefundPanel d={d} onChange={onChange} />
      <Alerts d={d} onChange={onChange} />
    </section>
  );
}
